import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Medichy | Privacy Policy</title>
        <meta
          name="description"
          content="Medichy's Privacy Policy outlines how we collect, use, and protect your personal information when you use our website and services."
        />
      </Helmet>
      <PrivacyPolicyWrapper>
        <PrivacyPolicyContainer>
          <PrivacyPolicyHeader>
            <h1>Privacy Policy</h1>
          </PrivacyPolicyHeader>

          <PrivacyPolicyContent>
            <Section>
              <p>
                We at Medichy know you care about how your personal information
                is used and shared, and we take your privacy seriously. Please
                read the following to learn more about our practices for
                collecting, using, maintaining, protecting and disclosing your
                information (the "Privacy Policy"). By using or accessing our
                website, medichy.com (the "Website"), you acknowledge that you
                accept the practices and policies outlined in this Privacy
                Policy, and you hereby consent that we will collect, use, and
                share your information in the following ways.
              </p>
            </Section>

            <Section>
              <SectionTitle>What Does This Privacy Policy Cover?</SectionTitle>
              <p>
                This Privacy Policy describes how we collect, use and disclose
                personal information ("personal information" or "information")
                that we gather when you are accessing or using our Website.
                Except as specifically provided below, this Privacy Policy does
                not apply to any other information collected by the Company by
                any other means, including on any other website operated by
                Company or any third party.
              </p>
              <p>
                When we refer to ourselves as "we", "us", or the "Company", we
                mean <strong>Medichy </strong> 
                and all of its subsidiary companies.
              </p>
              <p>
                If you do not agree with our policies and practices, your choice
                is not to use our Website. By accessing or using this Website,
                you agree to this privacy policy. This policy may change from
                time to time. Your continued use of this Website after we make
                changes is deemed acceptance of those changes, so please check
                the policy periodically for updates.
              </p>
            </Section>

            <Section>
              <SectionTitle>Information We Collect</SectionTitle>
              <p>
                We collect several types of information from and about users of
                our Website, directly from you, automatically through your use
                of our Website, and from third parties.
              </p>

              <Subsection>
                <SubsectionTitle>
                  Information We Collect Directly From You
                </SubsectionTitle>
                <p>
                  We collect personal information you choose to provide us, e.g.
                  information provided at the time of subscribing to our
                  newsletter or when completing any "Contact Us" form. We may
                  also ask you for information when you report a problem with
                  our Website or engage in any other form of correspondence with
                  us.
                </p>
              </Subsection>

              <Subsection>
                <SubsectionTitle>
                  Information We Collect Automatically
                </SubsectionTitle>
                <p>
                  As you navigate through and interact with our Website, we and
                  our third party business partners, including analytics and
                  advertising partners, may use automatic data collection
                  technologies to collect certain information such as your
                  browser type and operating system, web pages or content
                  interacted with on our Website, links you clicked, your IP
                  address or other identifiers, the length of time you visit our
                  Website, the referring URL or webpage that led you to our
                  Website, the information or content viewed, downloaded or
                  printed, and browsing actions and patterns.
                </p>
                <ul>
                  <li>
                    The information we collect automatically is only statistical
                    data and does not include personal information, but we may
                    maintain it or associate it with personal information we
                    collect in other ways or receive from third parties. It
                    helps us to improve our Website and to deliver a better and
                    more personalized service.
                  </li>
                </ul>
              </Subsection>

              <Subsection>
                <SubsectionTitle>
                  Automatic Data Collection Technologies
                </SubsectionTitle>
                <p>
                  The technologies we use for this automatic data collection may
                  include:
                </p>
                <ul>
                  <li>
                    <strong>Cookies (or browser cookies).</strong> A cookie is a
                    small file placed on the hard drive of your computer. You
                    may refuse to accept browser cookies by activating the
                    appropriate setting on your browser. However, if you select
                    this setting you may be unable to access certain parts of
                    our Website.
                  </li>
                  <li>
                    <strong>Web Beacons.</strong> Pages of our Website and our
                    emails may contain small electronic files known as web
                    beacons that permit the Company, for example, to count users
                    who have visited those pages or opened an email and for
                    other related website statistics.
                  </li>
                  <li>
                    <strong>Google Analytics and Google Tag Manager</strong>
                    <p>
                      We use Google Analytics, Google Tag Manager to collect and
                      analyze data about how users interact with our Website.
                      These tools help us better understand user behavior and
                      improve our Website's performance and content.
                    </p>
                  </li>
                </ul>
              </Subsection>
            </Section>

            <Section>
              <SectionTitle>How We Use Personal Information</SectionTitle>
              <p>We may use your personal information in the following ways:</p>

              <Subsection>
                <SubsectionTitle>To Serve You</SubsectionTitle>
                <p>We use your personal information to:</p>
                <ul>
                  <li>
                    Operate our business and present our Website and its
                    contents to you;
                  </li>
                  <li>
                    Provide you with information, products, or services that you
                    request from us;
                  </li>
                  <li>
                    Provide customer service and respond to requests or
                    inquiries; and
                  </li>
                  <li>
                    Provide you with newsletters, articles, announcements,
                    invitations, and other information.
                  </li>
                </ul>
              </Subsection>

              <Subsection>
                <SubsectionTitle>For Analytical Purposes</SubsectionTitle>
                <ul>
                  <li>
                    We and our third party business partners, including
                    advertising partners, may use your personal information to
                    analyze and track your interactions and movements around the
                    Website.
                  </li>
                </ul>
              </Subsection>
            </Section>

            <Section>
              <SectionTitle>
                How We Disclose Your Personal Information
              </SectionTitle>
              <p>
                We may disclose aggregated information about our users, and
                information that does not identify any individual, without
                restriction.
              </p>
              <p>
                We may disclose personal information that we collect or you
                provide as follows:
              </p>

              <Subsection>
                <SubsectionTitle>
                  To Our Subsidiaries and Affiliates
                </SubsectionTitle>
                <p>
                  Medichy and its subsidiaries and affiliates may disclose your
                  personal information amongst and between each other for the
                  purposes set forth in this Privacy Policy.
                </p>
              </Subsection>

              <Subsection>
                <SubsectionTitle>Business Transfers</SubsectionTitle>
                <p>
                  We may disclose your personal information to a buyer or other
                  successor in the event of a merger, divestiture,
                  restructuring, reorganization, dissolution, or other sale or
                  transfer of some or all of Medichy's assets.
                </p>
              </Subsection>

              <Subsection>
                <SubsectionTitle>Providers</SubsectionTitle>
                <p>
                  Medichy may disclose your personal information to contractors,
                  service providers, and other third parties we use to support
                  our business and who are bound by contractual obligations to
                  keep personal information confidential.
                </p>
              </Subsection>

              <Subsection>
                <SubsectionTitle>To Comply with Law</SubsectionTitle>
                <p>
                  We may disclose your personal information to comply with any
                  court order, law, or legal process, including to respond to
                  any government or regulatory request.
                </p>
              </Subsection>
            </Section>

            <Section>
              <SectionTitle>Your Rights and Choices</SectionTitle>
              <p>
                You have the following rights and choices with respect to your
                information.
              </p>

              <Subsection>
                <SubsectionTitle>Rights to Know</SubsectionTitle>
                <p>
                  Depending on where you live, you may have certain rights with
                  respect to your personal information:
                </p>
                <ul>
                  <li>Right to know what personal information we collect</li>
                  <li>
                    Right to access and receive a copy of your personal
                    information
                  </li>
                  <li>Right to correct or update personal information</li>
                  <li>Right to delete certain personal information</li>
                  <li>Right to opt-out of certain processing activities</li>
                </ul>
                <p>
                  If you would like to exercise any of these rights, please
                  contact us at our email:{" "}
                  <ContactEmail>privacy@medichy.com</ContactEmail>
                </p>
              </Subsection>

              <Subsection>
                <SubsectionTitle>Right to Delete</SubsectionTitle>
                <p>
                  You have the right to request that we delete any of your
                  personal information that we collected from you and retained,
                  subject to certain exceptions.
                </p>
              </Subsection>
            </Section>

            <Section>
              <SectionTitle>Cookies</SectionTitle>
              <p>
                Cookies are text files placed on your computer to collect
                standard Internet log information and visitor behavior
                information. When you visit our websites, we may collect
                information from you automatically through cookies or similar
                technology.
              </p>

              <Subsection>
                <SubsectionTitle>How do we use cookies?</SubsectionTitle>
                <p>
                  Our Company uses cookies in a range of ways to improve your
                  experience on our website, including:
                </p>
                <ul>
                  <li>Understanding how you use our website</li>
                  <li>Improving website functionality</li>
                </ul>
              </Subsection>

              <Subsection>
                <SubsectionTitle>
                  What types of cookies do we use?
                </SubsectionTitle>
                <p>
                  There are a number of different types of cookies, however, our
                  website uses:
                </p>
                <ul>
                  <li>
                    <strong>Functionality cookies</strong> – These cookies
                    remember your previously selected preferences such as
                    language preference.
                  </li>
                  <li>
                    <strong>Advertising cookies</strong> – These cookies collect
                    information about your visit to our website to provide you
                    with more relevant advertising.
                  </li>
                </ul>
              </Subsection>

              <Subsection>
                <SubsectionTitle>How to manage cookies</SubsectionTitle>
                <p>
                  You can set your browser not to accept cookies, however, in a
                  few cases, some of our website features may not function as a
                  result.
                </p>
              </Subsection>
            </Section>

            <Section>
              <SectionTitle>Privacy policies of other websites</SectionTitle>
              <p>
                The Medichy website contains links to other websites. Our
                privacy policy applies only to our website, so if you click on a
                link to another website, you should read their privacy policy.
              </p>
            </Section>

            <Section>
              <SectionTitle>Changes to our privacy policy</SectionTitle>
              <p>
                Our Company keeps its privacy policy under regular review and
                places any updates on this web page. This privacy policy was
                last updated on {new Date().getFullYear()}.
              </p>
            </Section>

            <ContactSection>
              <SectionTitle>How to contact us</SectionTitle>
              <p>
                If you have any questions about Medichy's privacy policy, the
                data we hold on you, or you would like to exercise one of your
                data protection rights, please do not hesitate to contact us.
              </p>
              <ContactInfo>
                <p>
                  Email us at: <ContactEmail>privacy@medichy.com</ContactEmail>
                </p>
              </ContactInfo>
            </ContactSection>
          </PrivacyPolicyContent>
        </PrivacyPolicyContainer>
      </PrivacyPolicyWrapper>
    </>
  );
};

// Styled Components
const PrivacyPolicyWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f8fafc;
`;

const PrivacyPolicyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media screen and (max-width: 900px) {
    padding: 3rem 1rem;
  }

  @media screen and (max-width: 600px) {
    padding: 2.5rem 1rem;
  }
`;

const PrivacyPolicyHeader = styled.header`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 3rem;
    font-weight: 700;
    color: #1a1a1a;
    position: relative;
    display: inline-block;

    &:after {
      content: "";
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: linear-gradient(135deg, #3182ce, #2c5282);
      border-radius: 2px;
    }
  }

  @media screen and (max-width: 900px) {
    margin-bottom: 3rem;

    h1 {
      font-size: 2.5rem;
    }
  }

  @media screen and (max-width: 600px) {
    h1 {
      font-size: 2rem;
    }
  }

  @media screen and (max-width: 420px) {
    h1 {
      font-size: 1.75rem;
    }
  }
`;

const PrivacyPolicyContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 4rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: 900px) {
    padding: 3rem;
  }

  @media screen and (max-width: 600px) {
    padding: 2rem;
    border-radius: 12px;
  }

  @media screen and (max-width: 420px) {
    padding: 1.5rem;
  }
`;

const Section = styled.section`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 600px) {
    margin-bottom: 2.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;

  &:after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #3182ce, #2c5282);
    border-radius: 2px;
  }

  @media screen and (max-width: 900px) {
    font-size: 1.5rem;
  }

  @media screen and (max-width: 600px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const Subsection = styled.div`
  margin-bottom: 2rem;
  padding-left: 1rem;
  border-left: 3px solid #e2e8f0;

  @media screen and (max-width: 600px) {
    padding-left: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const SubsectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1rem;

  @media screen and (max-width: 600px) {
    font-size: 1.1rem;
  }
`;

const ContactSection = styled(Section)`
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
  border-radius: 12px;
  padding: 2rem;
  border: 2px solid #bae6fd;

  @media screen and (max-width: 600px) {
    padding: 1.5rem;
  }
`;

const ContactInfo = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;

  p {
    margin: 0;
    font-size: 1.1rem;
    color: #4a5568;
  }

  @media screen and (max-width: 600px) {
    padding: 1rem;

    p {
      font-size: 1rem;
    }
  }
`;

const ContactEmail = styled.span`
  color: #3182ce;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
  }
`;

// Apply global styles for lists and paragraphs
const globalStyles = `
  ${PrivacyPolicyContent} p {
    font-size: 1.1rem;
    color: #4a5568;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  ${PrivacyPolicyContent} ul {
    list-style: none;
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;
  }

  ${PrivacyPolicyContent} li {
    font-size: 1.1rem;
    color: #4a5568;
    line-height: 1.6;
    margin-bottom: 0.75rem;
    position: relative;
    padding-left: 1.5rem;
  }

  ${PrivacyPolicyContent} li:before {
    content: "•";
    color: #3182ce;
    font-weight: bold;
    position: absolute;
    left: 0;
    font-size: 1.25rem;
  }

  ${PrivacyPolicyContent} strong {
    color: #1a1a1a;
    font-weight: 600;
  }

  @media screen and (max-width: 600px) {
    ${PrivacyPolicyContent} p,
    ${PrivacyPolicyContent} li {
      font-size: 1rem;
    }
    
    ${PrivacyPolicyContent} ul {
      padding-left: 1rem;
    }
    
    ${PrivacyPolicyContent} li {
      padding-left: 1.25rem;
    }
  }

  @media screen and (max-width: 420px) {
    ${PrivacyPolicyContent} p,
    ${PrivacyPolicyContent} li {
      font-size: 0.95rem;
    }
  }
`;

// Apply global styles
const StyleWrapper = styled.div`
  ${globalStyles}
`;

// Wrapper component to apply global styles
const PrivacyPolicyWithStyles = () => (
  <StyleWrapper>
    <PrivacyPolicy />
  </StyleWrapper>
);

export default PrivacyPolicyWithStyles;
